import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Button,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  deleteResident,
  listResidents,
  updateResident,
  unitLabel,
  type PublicResident,
} from '../../lib/residents-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

const STATUS_FILTERS = ['', 'ACTIVE', 'PENDING', 'SUSPENDED'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

export function ResidentsListPage() {
  const t = useT();
  const [residents, setResidents] = useState<PublicResident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PublicResident | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteResident(deleting.id);
      setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
      setInfo(t('res.deletedInfo', { name: deleting.full_name }));
      setDeleting(null);
    } catch (e) {
      const { code, message } = extractApiError(e);
      if (code === 'ERR_NOT_FOUND') {
        setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
        setInfo(t('res.alreadyRemoved'));
        setDeleting(null);
      } else {
        setDeleteError(message);
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setResidents(await listResidents());
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const shown = useMemo(
    () => (filter ? residents.filter((r) => r.status === filter) : residents),
    [residents, filter],
  );

  async function setStatus(r: PublicResident, status: 'ACTIVE' | 'SUSPENDED') {
    setBusyId(r.id);
    try {
      const updated = await updateResident(r.id, { status });
      setResidents((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    } catch (e) {
      alert(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4" fontWeight={600}>
          Residents
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('res.subtitle')}
        </Typography>
      </div>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          select
          size="small"
          label={t('common.status')}
          value={filter}
          onChange={(e) => setFilter(e.target.value as StatusFilter)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('res.statusAll')}</MenuItem>
          <MenuItem value="ACTIVE">{t('res.statusActive')}</MenuItem>
          <MenuItem value="PENDING">{t('res.statusPending')}</MenuItem>
          <MenuItem value="SUSPENDED">{t('res.statusSuspended')}</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary">
          {t('res.total', { count: residents.length })}
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {info ? (
        <Alert severity="info" onClose={() => setInfo(null)}>
          {info}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : shown.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {residents.length === 0
                  ? t('res.emptyAll')
                  : t('res.emptyFiltered')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.colName')}</TableCell>
                    <TableCell>{t('common.email')}</TableCell>
                    <TableCell sx={{ width: 170 }}>{t('res.colUnit')}</TableCell>
                    <TableCell sx={{ width: 120 }}>{t('common.status')}</TableCell>
                    <TableCell align="right" sx={{ width: 200 }}>
                      {t('common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        {r.full_name}
                        {r.is_primary ? (
                          <Chip
                            size="small"
                            label={t('res.mainUser')}
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={r.unit_block ? 'text.primary' : 'text.secondary'}
                        >
                          {unitLabel(r)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={r.status} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          {r.status === 'SUSPENDED' ? (
                            <Button
                              size="small"
                              onClick={() => setStatus(r, 'ACTIVE')}
                              disabled={busyId === r.id}
                            >
                              {t('res.reactivate')}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setStatus(r, 'SUSPENDED')}
                              disabled={busyId === r.id}
                            >
                              {t('res.suspend')}
                            </Button>
                          )}
                          <Tooltip title={t('common.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteError(null);
                                setDeleting(r);
                              }}
                              disabled={busyId === r.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!deleting}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('res.deleteTitle', { name: deleting?.full_name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('res.deleteWarning')}
            </Typography>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteBusy}>
            {deleteBusy ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function StatusChip({ status }: { status: PublicResident['status'] }) {
  const t = useT();
  const color =
    status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'error' : 'warning';
  const label =
    status === 'ACTIVE'
      ? t('res.chipActive')
      : status === 'SUSPENDED'
        ? t('res.chipSuspended')
        : t('res.chipPending');
  return <Chip size="small" label={label} color={color} />;
}
